// @flow

import React, { Component } from "react";
import type { RacePanelLink, RacesActiveBets } from "@fdr/types/Race";
import type { UserOptedInPromos } from "@tvg/types/User";
import { noop, isEmpty, get } from "lodash";
import buildRaceUrl from "@tvg/formatter/url";
import type { NullaryFn, UnaryFn, TernaryFn } from "@tvg/types/Functional";
import Icon from "@fdr/static-ui/Icons";
import TopRaceMask from "@fdr/static-ui/Masks/topRace";
import { arrowRight } from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import TopRace from "../../_organisms/TopRace";
import MagnetScrollArea from "../MagnetScrollArea";
import {
  Container,
  Title,
  UpperSection,
  LinkContainer,
  IconContainer,
  LastElement,
  TopRaceOutterContainer,
  LastElementContainer,
  NoRacesContainer
} from "./styled-components";

type Props = {
  races: RacePanelLink[],
  onRedirectToRace: UnaryFn<boolean, void>,
  onHandleTransition: UnaryFn<number, void>,
  onCardClick: TernaryFn<
    RacePanelLink,
    string,
    string | UserOptedInPromos,
    void
  >,
  optedInPromos: UserOptedInPromos,
  activeIndex: number,
  racesToRender: number,
  onLastCardClick: NullaryFn<void>,
  title?: string,
  isLoading: boolean,
  showLastCard: boolean,
  isOpeningApp: boolean,
  redirectToApp: NullaryFn<void>,
  showSilks: {
    [key: string]: boolean
  },
  racesActiveBets: RacesActiveBets,
  onSeeAllRaces: NullaryFn<void>,
  hasHeader?: boolean,
  trackTagText: string
};

class TopRaces extends Component<Props> {
  static defaultProps = {
    races: [],
    onRedirectToRace: noop,
    onHandleTransition: noop,
    onCardClick: noop,
    optedInPromos: {},
    activeIndex: 0,
    racesToRender: 0,
    title: "Top Races",
    onLastCardClick: noop,
    isLoading: false,
    showLastCard: false,
    isOpeningApp: false,
    redirectToApp: noop,
    showSilks: {},
    racesActiveBets: {},
    onSeeAllRaces: noop,
    hasHeader: true,
    trackTagText: ""
  };

  preventDefault = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();
    this.props.redirectToApp();
  };

  renderRaces = () =>
    this.props.races.map((race, index) => {
      const isHighlighted =
        (get(race, "highlighted.style", "") || "").toLowerCase() ===
        "highlighted";
      const betsCounter = get(
        this.props,
        `racesActiveBets.${race.tvgRaceId}`,
        0
      );

      const hasTopRace =
        this.props.activeIndex >= index - this.props.racesToRender &&
        this.props.activeIndex <= index + this.props.racesToRender &&
        Object.keys(this.props.showSilks).length > 0;

      return (
        <TopRaceOutterContainer
          key={`topRace-${race.raceId}`}
          data-qa-label={`topRace-${race.raceId}`}
          isHighlighted={isHighlighted}
          hasTopRace={hasTopRace}
          isOpeningApp={this.props.isOpeningApp}
          onClick={(e) => this.props.isOpeningApp && this.preventDefault(e)}
        >
          {this.props.activeIndex >= index - this.props.racesToRender &&
          this.props.activeIndex <= index + this.props.racesToRender &&
          Object.keys(this.props.showSilks).length > 0 ? (
            <TopRace
              onRunnerClick={this.props.onCardClick}
              optedInPromos={this.props.optedInPromos}
              onRedirectToRace={this.props.onRedirectToRace}
              key={`topRace-${race.raceId}`}
              race={race}
              isLastRace={false}
              isNotLastRunner={index < this.props.races.length - 1}
              raceCardUrl={buildRaceUrl(
                race.trackCode,
                race.trackName,
                +race.raceNumber,
                race.isGreyhound
              )}
              onCardClick={this.props.onCardClick}
              data-qa-label={`topRaceContainer-${race.raceId}`}
              betsCounter={betsCounter}
              showSilks={this.props.showSilks}
              isHighlighted={isHighlighted}
              trackTagText={this.props.trackTagText}
            />
          ) : (
            <TopRaceMask
              key={`topRaceMask-${race.raceId}`}
              data-qa-label={`topRaceContainer-${race.raceId}`}
            />
          )}
        </TopRaceOutterContainer>
      );
    });

  renderSeeAllLink = () => (
    <LinkContainer
      onClick={this.props.onSeeAllRaces}
      data-qa-label="topRaceLastElement-Link"
    >
      <IconContainer data-qa-label="topRaceLastElement-IconContainer">
        <Icon
          icon={arrowRight}
          color={buildColor("blue", "D1")}
          size={24}
          qaLabel="topRaceLastElement-Icon"
        />
      </IconContainer>
      See all races
    </LinkContainer>
  );

  render() {
    const noRaces = !this.props.isLoading && this.props.races.length === 0;

    return (
      <Container
        hasHeader={this.props.hasHeader}
        data-qa-label="topRacesContainer"
      >
        {this.props.hasHeader && (
          <UpperSection>
            <Title data-qa-label="topRacesTitle">{this.props.title}</Title>
          </UpperSection>
        )}
        {noRaces ? (
          <NoRacesContainer>{this.renderSeeAllLink()}</NoRacesContainer>
        ) : (
          <MagnetScrollArea
            onHandleTransition={this.props.onHandleTransition}
            activeIndex={this.props.activeIndex}
            isLoading={this.props.isLoading}
          >
            {this.renderRaces()}
            {this.props.showLastCard || isEmpty(this.props.races) ? (
              <LastElementContainer
                key="TopRacesLastElement"
                isFirstElement={!this.props.races.length}
                data-qa-label="topRaceLastElement"
              >
                <LastElement>{this.renderSeeAllLink()}</LastElement>
              </LastElementContainer>
            ) : (
              []
            )}
          </MagnetScrollArea>
        )}
      </Container>
    );
  }
}

export default TopRaces;
